import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Group, Loader, NumberInput, SegmentedControl, Select, SimpleGrid, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconClockExclamation } from '@tabler/icons-react';
import { collection, doc, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { EtfConfig, EtfRequest, EtfRequestStatus, EtfRequestType, Fund } from '../../types';
import { useFirestore, useFunctions } from '../../helpers/firebaseContext';
import useFirestoreCollection from '../../helpers/useFirestoreCollection';
import useFirestoreDoc from '../../helpers/useFirestoreDoc';
import { isEtfSectionError, isPastCutoff, kuwaitDateString, kuwaitDateTimeString, useEtfContext } from './etf_common';

//AP creation/redemption requests (funds/{id}/etf_requests). Every button calls a functions-geneva callable
//(etf_requests.ts) that re-checks the status, the cut-off and the snapshot day - the page only reads.

const STATUS: Record<EtfRequestStatus, { label: string; color: string }> = {
    pending_fm_approval: { label: 'Waiting for FM', color: 'yellow' },
    pending_operations: { label: 'Waiting for Operations', color: 'orange' },
    with_kcc: { label: 'With KCC', color: 'blue' },
    pending_geneva_booking: { label: 'Book in Geneva', color: 'grape' },
    booked: { label: 'Booked', color: 'teal' },
    rejected: { label: 'Rejected by FM', color: 'red' },
    kcc_rejected: { label: 'Rejected by KCC', color: 'red' },
    cancelled: { label: 'Cancelled', color: 'gray' },
};

const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 3 });
const typeLabel = (type: EtfRequestType) => (type === 'subscription' ? 'Subscription (creation)' : 'Redemption');

//minute ticker so the cut-off banner flips without a reload
const useMinute = () => {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 30_000);
        return () => clearInterval(timer);
    }, []);
    return now;
};

//runs a callable (once per payload, in order) with one notification; returns true on success. busyKey marks which
//button spins - e.g. one Retry row instead of all of them. Notifications sit top-center so they never cover the buttons.
const useEtfCall = () => {
    const functions = useFunctions();
    const [busy, setBusy] = useState<string | null>(null);
    const call = async (name: string, payload: Record<string, unknown> | Record<string, unknown>[], success: string, busyKey = name): Promise<boolean> => {
        setBusy(busyKey);
        const id = notifications.show({ position: 'top-center', loading: true, title: 'Working...', message: name, autoClose: false });
        try {
            for (const p of Array.isArray(payload) ? payload : [payload]) {
                await httpsCallable(functions, name)(p);
            }
            notifications.update({ id, position: 'top-center', color: 'teal', loading: false, autoClose: 2500, title: 'Done', message: success });
            return true;
        } catch (e) {
            notifications.update({ id, position: 'top-center', color: 'red', loading: false, autoClose: 6000, title: 'Failed', message: e instanceof Error ? e.message : String(e) });
            return false;
        } finally {
            setBusy(null);
        }
    };
    return { call, busy };
};

const NewRequest = ({ fund }: { fund: Fund }) => {
    const { data } = useEtfContext();
    const { call, busy } = useEtfCall();
    const [type, setType] = useState<EtfRequestType>('subscription');
    const [baskets, setBaskets] = useState<number>(1);

    const pcf = data && !isEtfSectionError(data.pcf) ? data.pcf : null;
    if (!pcf) return <Text c="dimmed">The basket is not available yet — see the PCF / Basket tab.</Text>;

    const securities = pcf.basketMarketValueBase * baskets;
    const units = pcf.sharesPerCreationUnit * baskets;
    const cash = units * pcf.officialNav - securities; //balancing cash: basket + cash = units x NAV (as etf_requests.ts books it)

    return (
        <Card withBorder padding="md" mb="lg">
            <Title order={4} mb="sm">New request (AP)</Title>
            {pcf.navPositionDateMismatch ? (
                <Alert color="red" icon={<IconAlertTriangle />} mb="sm">
                    The basket can't be used: position {pcf.positionAsOf} and NAV {pcf.navDate} dates differ. Sync the NAV, then Refresh Data.
                </Alert>
            ) : pcf.stale && (
                <Alert color="yellow" icon={<IconAlertTriangle />} mb="sm">
                    Priced at the NAV of {pcf.navDate}. Fine after a weekend or holiday — otherwise Sync NAV first.
                </Alert>
            )}
            <Group align="flex-end" mb="sm">
                <SegmentedControl value={type} onChange={(v) => setType(v as EtfRequestType)} data={[{ value: 'subscription', label: 'Subscription' }, { value: 'redemption', label: 'Redemption' }]} />
                <NumberInput label="Baskets" min={1} allowDecimal={false} value={baskets} onChange={(v) => setBaskets(Math.max(1, Number(v) || 1))} w={120} />
                <Button
                    loading={busy === 'submitEtfRequest'}
                    disabled={pcf.navPositionDateMismatch}
                    onClick={() => call('submitEtfRequest', { fundId: fund.id, type, baskets }, 'Request submitted — waiting for the FM')}
                >
                    Submit
                </Button>
            </Group>
            <Text size="sm" mb="xs">
                {type === 'subscription'
                    ? <>You deliver the securities below plus <b>{fmt(cash)} {pcf.baseCurrency}</b> cash and receive <b>{units.toLocaleString()}</b> ETF units.</>
                    : <>You deliver <b>{units.toLocaleString()}</b> ETF units and receive the securities below plus <b>{fmt(cash)} {pcf.baseCurrency}</b> cash.</>}
                {' '}Indicative from today's basket (close of {pcf.positionAsOf}); submitting locks it.
            </Text>
            <Table striped withTableBorder>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Ticker</Table.Th>
                        <Table.Th>Security</Table.Th>
                        <Table.Th ta="right">Quantity</Table.Th>
                        <Table.Th ta="right">Price</Table.Th>
                        <Table.Th ta="right">Value</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {pcf.constituents.map((c) => (
                        <Table.Tr key={c.securityCode}>
                            <Table.Td>{c.alternativeTicker}</Table.Td>
                            <Table.Td>{c.securityDescription}</Table.Td>
                            <Table.Td ta="right">{(c.basketQuantity * baskets).toLocaleString()}</Table.Td>
                            <Table.Td ta="right">{c.marketPriceLocal.toFixed(3)}</Table.Td>
                            <Table.Td ta="right">{fmt(c.marketValueBase * baskets)}</Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
                <Table.Tfoot>
                    <Table.Tr><Table.Td colSpan={4}>Securities</Table.Td><Table.Td ta="right">{fmt(securities)}</Table.Td></Table.Tr>
                    <Table.Tr><Table.Td colSpan={4}>Cash component</Table.Td><Table.Td ta="right">{fmt(cash)}</Table.Td></Table.Tr>
                    <Table.Tr fw={700}><Table.Td colSpan={4}>Total</Table.Td><Table.Td ta="right">{fmt(securities + cash)}</Table.Td></Table.Tr>
                </Table.Tfoot>
            </Table>
        </Card>
    );
};

const SnapshotTable = ({ request }: { request: EtfRequest }) => (
    <Table striped withTableBorder mb="md">
        <Table.Thead>
            <Table.Tr>
                <Table.Th>Ticker</Table.Th>
                <Table.Th>Security</Table.Th>
                <Table.Th>Geneva code</Table.Th>
                <Table.Th ta="right">Quantity</Table.Th>
                <Table.Th ta="right">Price ({request.snapshot.positionAsOf})</Table.Th>
                <Table.Th ta="right">Value</Table.Th>
            </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
            {request.snapshot.constituents.map((c) => (
                <Table.Tr key={c.securityCode}>
                    <Table.Td>{c.ticker}</Table.Td>
                    <Table.Td>{c.name}</Table.Td>
                    <Table.Td>{c.securityCode}</Table.Td>
                    <Table.Td ta="right">{c.quantity.toLocaleString()}</Table.Td>
                    <Table.Td ta="right">{c.price.toFixed(3)}</Table.Td>
                    <Table.Td ta="right">{fmt(c.value)}</Table.Td>
                </Table.Tr>
            ))}
        </Table.Tbody>
        <Table.Tfoot>
            <Table.Tr><Table.Td colSpan={5}>Securities</Table.Td><Table.Td ta="right">{fmt(request.snapshot.securitiesValue)}</Table.Td></Table.Tr>
            <Table.Tr><Table.Td colSpan={5}>Cash component</Table.Td><Table.Td ta="right">{fmt(request.snapshot.cashComponent)}</Table.Td></Table.Tr>
            <Table.Tr fw={700}><Table.Td colSpan={5}>Total ({request.units.toLocaleString()} units, NAV {request.snapshot.navPerUnit} on {request.snapshot.navDate})</Table.Td><Table.Td ta="right">{fmt(request.snapshot.total)}</Table.Td></Table.Tr>
        </Table.Tfoot>
    </Table>
);

//exactly what "Book in Geneva" publishes, with the ContributedCost check
const GenevaPreview = ({ request }: { request: EtfRequest }) => {
    const records = request.genevaRecords;
    if (!records) return null;
    const lines = (records.journal.AdjLines ?? []) as { FinAcctCode: string; DebitAmount: number; CreditAmount: number }[];
    const legsTotal = Math.round(records.legs.reduce((s, l) => s + Number(l.record.Quantity) * Number(l.record.Price), 0) * 1000) / 1000;
    const contributed = lines.find((l) => l.FinAcctCode === 'ContributedCost');
    const contributedAmount = contributed ? contributed.DebitAmount || contributed.CreditAmount : 0;
    const nets = Math.abs(legsTotal - contributedAmount) < 0.0005;
    return (
        <>
            <Text fw={700} mb="xs">Geneva records — booked on {request.bookingDate}</Text>
            <Alert color={nets ? 'teal' : 'red'} mb="sm" icon={nets ? undefined : <IconAlertTriangle />}>
                {records.legs[0]?.recordType ?? 'Legs'} total {fmt(legsTotal)} vs journal ContributedCost {fmt(contributedAmount)} — {nets ? 'ContributedCost nets to 0' : 'MISMATCH, do not book'}
            </Alert>
            <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
                <Table withTableBorder>
                    <Table.Thead>
                        <Table.Tr><Table.Th>Record</Table.Th><Table.Th>Investment</Table.Th><Table.Th ta="right">Qty</Table.Th><Table.Th ta="right">Price</Table.Th></Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {records.legs.map((l) => (
                            <Table.Tr key={l.key}>
                                <Table.Td>{l.recordType}</Table.Td>
                                <Table.Td>{String(l.record.Investment)}</Table.Td>
                                <Table.Td ta="right">{Number(l.record.Quantity).toLocaleString()}</Table.Td>
                                <Table.Td ta="right">{Number(l.record.Price).toFixed(3)}</Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
                <Table withTableBorder>
                    <Table.Thead>
                        <Table.Tr><Table.Th>Journal account</Table.Th><Table.Th ta="right">Debit</Table.Th><Table.Th ta="right">Credit</Table.Th></Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {lines.map((l) => (
                            <Table.Tr key={l.FinAcctCode}>
                                <Table.Td>{l.FinAcctCode}</Table.Td>
                                <Table.Td ta="right">{l.DebitAmount ? fmt(l.DebitAmount) : ''}</Table.Td>
                                <Table.Td ta="right">{l.CreditAmount ? fmt(l.CreditAmount) : ''}</Table.Td>
                            </Table.Tr>
                        ))}
                        <Table.Tr><Table.Td>GWI {request.type}</Table.Td><Table.Td colSpan={2} ta="right">{request.units.toLocaleString()} units for the AP</Table.Td></Table.Tr>
                    </Table.Tbody>
                </Table>
            </SimpleGrid>
        </>
    );
};

const RequestDetail = ({ request, fund, config, now }: { request: EtfRequest; fund: Fund; config: EtfConfig | undefined; now: Date }) => {
    const { call, busy } = useEtfCall();
    const [reason, setReason] = useState('');
    const [rejectStage, setRejectStage] = useState<string | null>('request');
    const ref = { fundId: fund.id, requestId: request.id };
    const today = kuwaitDateString(now);
    const staleSnapshot = request.snapshot.date !== today;
    const pastCutoff = isPastCutoff(now, fund.etf_cutoff);
    const simulated = config?.kcc_mode === 'simulated';
    const done = (ok: boolean) => { if (ok) setReason(''); };

    const actions = Object.entries(request.actions ?? {})
        .filter(([, a]) => a)
        .sort(([, a], [, b]) => (a!.at?.toMillis() ?? 0) - (b!.at?.toMillis() ?? 0));

    const refreshButton = (
        <Button variant="default" loading={busy === 'refreshEtfSnapshot'} onClick={() => call('refreshEtfSnapshot', ref, 'Snapshot refreshed — back to the FM for approval')}>
            Refresh snapshot
        </Button>
    );
    const cancelButton = (
        <Button variant="subtle" color="gray" loading={busy === 'cancelEtfRequest'} onClick={() => call('cancelEtfRequest', { ...ref, reason }, 'Request cancelled')}>
            Cancel request
        </Button>
    );

    return (
        <Card withBorder padding="md">
            <Group justify="space-between" mb="sm">
                <div>
                    <Title order={4}>{typeLabel(request.type)} · {request.baskets} basket{request.baskets > 1 ? 's' : ''} · {request.units.toLocaleString()} units</Title>
                    <Text size="sm" c="dimmed">Request {request.id} · basket of {request.snapshot.date} (prices close {request.snapshot.positionAsOf})</Text>
                </div>
                <Badge size="lg" color={STATUS[request.status].color}>{STATUS[request.status].label}</Badge>
            </Group>

            <Group gap="lg" mb="md">
                {actions.map(([name, a]) => (
                    <Text key={name} size="xs"><b>{name.replace('_', ' ')}</b> {a!.by} · {kuwaitDateTimeString(a!.at?.toDate())}{a!.reason ? ` · “${a!.reason}”` : ''}</Text>
                ))}
            </Group>
            {request.lateApproval && (
                <Alert color="orange" icon={<IconClockExclamation />} mb="md" title="Sent after the cut-off">
                    {request.lateApproval.by} included it in the {request.lateApproval.date} NAV: “{request.lateApproval.reason}”
                </Alert>
            )}

            <SnapshotTable request={request} />

            {request.status === 'pending_fm_approval' && (
                <Stack>
                    {staleSnapshot && <Alert color="yellow" icon={<IconAlertTriangle />}>This basket is from {request.snapshot.date}. Refresh it before approving — Operations can only send today's basket.</Alert>}
                    <Group align="flex-end">
                        <Button color="teal" loading={busy === 'approveEtfRequest'} onClick={() => call('approveEtfRequest', ref, 'Approved — waiting for Operations')}>Approve (FM)</Button>
                        <TextInput label="Reason (reject / cancel)" value={reason} onChange={(e) => setReason(e.currentTarget.value)} w={320} />
                        <Button color="red" variant="light" disabled={!reason.trim()} loading={busy === 'rejectEtfRequest'} onClick={async () => done(await call('rejectEtfRequest', { ...ref, reason }, 'Request rejected'))}>Reject</Button>
                        {staleSnapshot && refreshButton}
                        {cancelButton}
                    </Group>
                </Stack>
            )}

            {request.status === 'pending_operations' && (
                <Stack>
                    {staleSnapshot ? (
                        <Alert color="yellow" icon={<IconAlertTriangle />} title="Basket is not today's">
                            This basket is from {request.snapshot.date}. Refresh it — the new prices go back to the FM for approval before it can be sent.
                        </Alert>
                    ) : pastCutoff ? (
                        <Alert color="red" variant="filled" icon={<IconClockExclamation />} title={`PAST THE ${fund.etf_cutoff} CUT-OFF`}>
                            Sending now puts this {request.type} in today's NAV. Only do it after confirming with Operations, and record why.
                            Otherwise leave it here and refresh the snapshot on the next business day.
                        </Alert>
                    ) : null}
                    <Group align="flex-end">
                        {staleSnapshot ? refreshButton : pastCutoff ? (
                            <>
                                <TextInput label="Late reason (required)" placeholder="Confirmed with Operations by ..." value={reason} onChange={(e) => setReason(e.currentTarget.value)} w={360} />
                                <Button color="red" disabled={!reason.trim()} loading={busy === 'sendEtfRequestToKcc'} onClick={async () => done(await call('sendEtfRequestToKcc', { ...ref, lateReason: reason }, 'Sent to KCC (late, included in today\'s NAV)'))}>
                                    Send late (included in today's NAV)
                                </Button>
                            </>
                        ) : (
                            <Button disabled={!fund.etf_cutoff} loading={busy === 'sendEtfRequestToKcc'} onClick={() => call('sendEtfRequestToKcc', ref, 'Sent to KCC')}>Send to KCC (Operations)</Button>
                        )}
                        {cancelButton}
                    </Group>
                </Stack>
            )}

            {request.kcc && (
                <Card withBorder padding="sm" mt="md" mb="md">
                    <Group justify="space-between" mb="xs">
                        <Text fw={700}>KCC {request.kcc.mode === 'simulated' ? '(simulated)' : ''} · {request.kcc.status}</Text>
                        <Text size="xs" c="dimmed">Basket {request.kcc.basketRef ?? '—'} · Request {request.kcc.requestRef ?? '—'} · AP BPID {request.kcc.apBpid ?? 'not set'}</Text>
                    </Group>
                    <Group gap="xs">
                        {request.kcc.history.map((h, i) => (
                            <Badge key={i} variant="light" color={h.status === 'Rejected' ? 'red' : h.status === 'Processed' ? 'teal' : 'blue'}>
                                {h.status} {new Date(h.at).toLocaleTimeString('en-GB', { timeZone: 'Asia/Kuwait' })}{h.note ? ` — ${h.note}` : ''}
                            </Badge>
                        ))}
                    </Group>
                    {request.kcc.rejectReason && <Text c="red" size="sm" mt="xs">Rejected at the {request.kcc.rejectStage} stage: {request.kcc.rejectReason}</Text>}
                    {request.status === 'with_kcc' && request.kcc.status === 'Created' && simulated && (
                        <Group align="flex-end" mt="sm">
                            <Button color="teal" variant="light" loading={busy === 'simulateKcc'} onClick={() => call('simulateKcc', { ...ref, outcome: 'success' }, 'KCC processed (simulated)')}>
                                Simulate KCC success
                            </Button>
                            <Select label="Reject stage" value={rejectStage} onChange={setRejectStage} data={[{ value: 'basket', label: 'Transaction basket' }, { value: 'request', label: typeLabel(request.type) }]} w={220} />
                            <TextInput label="KCC reason" placeholder="e.g. Insufficient AP balance" value={reason} onChange={(e) => setReason(e.currentTarget.value)} w={280} />
                            <Button color="red" variant="light" disabled={!reason.trim()} loading={busy === 'simulateKcc'} onClick={async () => done(await call('simulateKcc', { ...ref, outcome: 'reject', stage: rejectStage, reason }, 'KCC rejected (simulated)'))}>
                                Simulate KCC reject
                            </Button>
                        </Group>
                    )}
                </Card>
            )}

            {(request.status === 'pending_geneva_booking' || request.status === 'booked') && <GenevaPreview request={request} />}

            {(request.status === 'booked' || request.status === 'pending_geneva_booking') && (() => {
                const booked = request.status === 'booked';
                const responses = request.geneva_responses ?? {};
                const statusOf = (key: string) => (responses[key]?.status ?? '').toLowerCase();
                //booked: every record booking publishes (legs, journal, GWI order) plus whatever the loader wrote back, so a
                //record with no response still shows and can be retried. After an undo: only rows Geneva still has.
                const expected = booked ? [...(request.genevaRecords?.legs ?? []).map((l) => l.key), 'journal', 'gwi_order'] : [];
                const keys = [...new Set([...expected, ...Object.keys(responses)])].sort((a, b) => a.localeCompare(b));
                const inFlight = (key: string) => ['adding', 'deleting'].includes(statusOf(key));
                const retryable = (key: string) => booked && statusOf(key) !== 'success' && key !== 'gwi_wire_instructions';
                //Retry all skips in-flight rows: Geneva may still be loading them
                const failed = keys.filter((key) => retryable(key) && !inFlight(key));
                const undoing = !booked && keys.length > 0;
                const undo = () => {
                    if (window.confirm('Delete everything this request booked in Geneva (GWI order, journal, legs) and put it back on "Book in Geneva"?')) {
                        call('undoEtfGenevaBooking', ref, 'Undo queued — rows disappear as Geneva deletes them', 'undo');
                    }
                };
                const badgeColor = (key: string) => {
                    const status = statusOf(key);
                    if (status === 'success') return 'teal';
                    if (status === 'adding') return 'blue';
                    if (status === 'deleting') return 'orange';
                    return responses[key] ? 'red' : 'gray';
                };
                return (
                    <>
                        <Group justify="space-between" mb="xs">
                            {booked ? <span /> : (
                                <Button color="grape" disabled={undoing} loading={busy === 'bookEtfRequestInGeneva'} onClick={() => call('bookEtfRequestInGeneva', ref, 'Queued for Geneva — results appear below')}>
                                    Book in Geneva (Operations)
                                </Button>
                            )}
                            <Group gap="xs">
                                {booked && (
                                    <Button size="xs" color="red" variant="light" disabled={failed.length === 0 || busy != null} loading={busy === 'retry:all'}
                                        onClick={() => call('retryEtfGenevaRecord', failed.map((key) => ({ ...ref, key })), `${failed.length} record${failed.length > 1 ? 's' : ''} re-queued`, 'retry:all')}>
                                        Retry all failed ({failed.length})
                                    </Button>
                                )}
                                {(booked || undoing) && (
                                    <Button size="xs" color="red" disabled={busy != null && busy !== 'undo'} loading={busy === 'undo'} onClick={undo}>
                                        {booked ? 'Undo booking' : 'Undo again'}
                                    </Button>
                                )}
                            </Group>
                        </Group>
                        {undoing && (
                            <Alert color="orange" icon={<IconAlertTriangle />} mb="xs">
                                Undoing the Geneva booking — Book in Geneva unlocks once every row below is gone. If a delete fails, fix it and press Undo again.
                            </Alert>
                        )}
                        {keys.length > 0 && (
                            <Table withTableBorder>
                                <Table.Thead>
                                    <Table.Tr><Table.Th>Geneva record</Table.Th><Table.Th>Status</Table.Th><Table.Th>Message</Table.Th><Table.Th /></Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {keys.map((key) => (
                                        <Table.Tr key={key}>
                                            <Table.Td>{key}</Table.Td>
                                            <Table.Td><Badge color={badgeColor(key)}>{responses[key]?.status ?? 'not sent'}</Badge></Table.Td>
                                            <Table.Td><Text size="xs">{responses[key]?.status_message ?? ''}</Text></Table.Td>
                                            <Table.Td>
                                                {retryable(key) && (
                                                    <Button size="xs" variant="light" disabled={busy != null && busy !== `retry:${key}`} loading={busy === `retry:${key}`}
                                                        onClick={() => call('retryEtfGenevaRecord', { ...ref, key }, `${key} re-queued`, `retry:${key}`)}>
                                                        Retry
                                                    </Button>
                                                )}
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        )}
                    </>
                );
            })()}
        </Card>
    );
};

const EtfRequests = () => {
    const { fund } = useEtfContext();
    const db = useFirestore();
    const now = useMinute();
    const [selected, setSelected] = useState<string | null>(null);

    const { data: config } = useFirestoreDoc<EtfConfig>(doc(db, 'config', 'etf'));
    const requestsQuery = query(collection(db, 'funds', fund.id, 'etf_requests'), orderBy('createdAt', 'desc'));
    const { data, loading } = useFirestoreCollection(requestsQuery, true);
    const requests = (data ?? []) as EtfRequest[];
    const current = requests.find((r) => r.id === selected) ?? null;
    const unbooked = requests.filter((r) => r.status === 'pending_geneva_booking');
    const pastCutoff = isPastCutoff(now, fund.etf_cutoff);

    return (
        <>
            {pastCutoff && (
                <Alert color="red" variant="filled" icon={<IconClockExclamation />} title={`Past the ${fund.etf_cutoff} cut-off`} mb="md">
                    Requests sent to KCC now are NOT in today's NAV unless Operations confirms and sends them late with a reason.
                </Alert>
            )}
            {!fund.etf_cutoff && (
                <Alert color="orange" icon={<IconAlertTriangle />} title="Cut-off not configured" mb="md">
                    Set <code>etf_cutoff</code> (e.g. <code>"14:00"</code>) on the fund document — nothing can be sent to KCC without it.
                </Alert>
            )}
            {unbooked.length > 0 && (
                <Alert color="grape" icon={<IconAlertTriangle />} title="Processed by KCC, not booked in Geneva" mb="md">
                    {unbooked.length} request{unbooked.length > 1 ? 's' : ''} must be booked in Geneva before today's NAV is calculated.
                </Alert>
            )}
            {config?.kcc_mode !== 'simulated' && (
                <Alert color="yellow" icon={<IconAlertTriangle />} mb="md">
                    <code>config/etf.kcc_mode</code> is not <code>"simulated"</code> and the real KCC API is not integrated yet — sending to KCC will fail.
                </Alert>
            )}

            <NewRequest fund={fund} />

            <Title order={4} mb="sm">Requests</Title>
            {loading && !data ? <Loader /> : requests.length === 0 ? <Text c="dimmed" mb="md">No requests yet.</Text> : (
                <Table highlightOnHover withTableBorder mb="lg">
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Submitted</Table.Th>
                            <Table.Th>Type</Table.Th>
                            <Table.Th ta="right">Baskets</Table.Th>
                            <Table.Th ta="right">Units</Table.Th>
                            <Table.Th ta="right">Value ({fund.currency})</Table.Th>
                            <Table.Th>Basket day</Table.Th>
                            <Table.Th>Status</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {requests.map((r) => (
                            <Table.Tr key={r.id} onClick={() => setSelected(r.id ?? null)} style={{ cursor: 'pointer' }} bg={r.id === selected ? 'var(--mantine-color-default-hover)' : undefined}>
                                <Table.Td>{kuwaitDateTimeString(r.createdAt?.toDate())}<Text size="xs" c="dimmed">{r.actions?.submitted?.by}</Text></Table.Td>
                                <Table.Td>{typeLabel(r.type)}</Table.Td>
                                <Table.Td ta="right">{r.baskets}</Table.Td>
                                <Table.Td ta="right">{r.units.toLocaleString()}</Table.Td>
                                <Table.Td ta="right">{fmt(r.snapshot.total)}</Table.Td>
                                <Table.Td>{r.snapshot.date}</Table.Td>
                                <Table.Td><Badge color={STATUS[r.status].color}>{STATUS[r.status].label}</Badge></Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            )}

            {current && <RequestDetail key={current.id} request={current} fund={fund} config={config ?? undefined} now={now} />}
        </>
    );
};

export default EtfRequests;
